import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { z } from "zod";

dayjs.extend(customParseFormat);

function parseOdometerValue(value: string): number {
	const onlyDigits = value.replace(/\D/g, "");
	if (onlyDigits.length === 0) {
		return Number.NaN;
	}
	return Number(onlyDigits);
}

type ManualUpdateData = {
	newValue: string;
	useCurrentDateTime: boolean;
	customDate: CalendarDate | null;
	customTime: string;
	reason: string | null;
	evidenceName: string;
	evidenceFile: File | null;
};

const TIME_24H_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

function addIssue(
	ctx: z.RefinementCtx,
	path: keyof ManualUpdateData,
	message: string
): void {
	ctx.addIssue({
		code: z.ZodIssueCode.custom,
		path: [path],
		message,
	});
}

function validateNewValue(
	data: ManualUpdateData,
	ctx: z.RefinementCtx
): number | null {
	const parsedNewValue = parseOdometerValue(data.newValue);
	if (!Number.isFinite(parsedNewValue) || parsedNewValue <= 0) {
		addIssue(ctx, "newValue", "Informe um valor válido de hodômetro");
		return null;
	}
	return parsedNewValue;
}

function validateRetroactiveDateTime(
	data: ManualUpdateData,
	ctx: z.RefinementCtx
): void {
	if (data.useCurrentDateTime) {
		return;
	}
	if (data.customDate === null) {
		addIssue(ctx, "customDate", "Escolha a data da leitura");
	}
	if (data.customTime.trim() === "") {
		addIssue(ctx, "customTime", "Escolha a hora da leitura");
		return;
	}
	const isValid24h = TIME_24H_REGEX.test(data.customTime.trim());
	if (!isValid24h) {
		addIssue(ctx, "customTime", "Informe uma hora válida (00:00 até 23:59)");
		return;
	}
	if (data.customDate === null) {
		return;
	}
	const nowCalendar = today(getLocalTimeZone());
	if (data.customDate.compare(nowCalendar) > 0) {
		addIssue(ctx, "customDate", "Não é permitido informar data futura");
		return;
	}
	const trimmedTime = data.customTime.trim();
	const readingAt = dayjs(
		`${data.customDate.year}-${data.customDate.month}-${data.customDate.day} ${trimmedTime}`,
		"YYYY-M-D HH:mm"
	);
	if (!readingAt.isValid()) {
		addIssue(ctx, "customTime", "Informe uma hora válida (00:00 até 23:59)");
		return;
	}
	if (readingAt.isAfter(dayjs())) {
		addIssue(ctx, "customTime", "Não é permitido informar data e hora futuras");
	}
}

const ALLOWED_EVIDENCE_EXTENSIONS = [
	".png",
	".jpg",
	".jpeg",
	".webp",
	".pdf",
] as const;

function validateEvidenceFileType(
	data: ManualUpdateData,
	ctx: z.RefinementCtx
): void {
	if (data.evidenceFile === null) {
		return;
	}
	const lowerName = data.evidenceFile.name.toLowerCase();
	const isAllowed = ALLOWED_EVIDENCE_EXTENSIONS.some((ext) =>
		lowerName.endsWith(ext)
	);
	if (!isAllowed) {
		addIssue(
			ctx,
			"evidenceFile",
			"Envie apenas arquivos PNG, JPG, JPEG, WEBP ou PDF."
		);
	}
}

function validateRegressionRules(
	data: ManualUpdateData,
	ctx: z.RefinementCtx,
	parsedNewValue: number,
	currentOdometer: number
): void {
	if (parsedNewValue >= currentOdometer) {
		return;
	}
	if ((data.reason ?? "").trim() === "") {
		addIssue(ctx, "reason", "Motivo do ajuste é obrigatório para valor menor");
	}
	if (data.evidenceFile === null) {
		addIssue(ctx, "evidenceFile", "Evidência é obrigatória para valor menor");
	}
}

export const createOdometerManualUpdateSchema = (currentOdometer: number) =>
	z
		.object({
			newValue: z.string().min(1, "Novo hodômetro é obrigatório"),
			useCurrentDateTime: z.boolean(),
			customDate: z.instanceof(CalendarDate).nullable(),
			customTime: z.string(),
			reason: z.string().nullable(),
			evidenceName: z.string(),
			evidenceFile: z.instanceof(File).nullable(),
		})
		.superRefine((data, ctx) => {
			const parsedNewValue = validateNewValue(data, ctx);
			if (parsedNewValue === null) {
				return;
			}
			validateRetroactiveDateTime(data, ctx);
			validateEvidenceFileType(data, ctx);
			validateRegressionRules(data, ctx, parsedNewValue, currentOdometer);
		});

export type OdometerManualUpdateSchema = z.infer<
	ReturnType<typeof createOdometerManualUpdateSchema>
>;
